import { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Message } from 'sharedUi/Message';
import { uploadAttachment, type UploadedAttachment } from '../../services/email.service';
import { extractErrorMessage } from '../../utils/error-handler';

/**
 * What an attachment field currently holds.
 *
 * A union rather than a bare string because the channels store the three cases
 * differently — push keeps a url, email keeps `[{fileId, filename, …}]` or a
 * `"{parameter}"` string — and a component that flattened them to text would
 * make every caller re-parse its own storage to find out which it had.
 */
export type AttachmentValue =
  | { kind: 'none' }
  | { kind: 'parameter'; name: string }
  | { kind: 'url'; url: string }
  | { kind: 'file'; file: UploadedAttachment };

interface AttachmentFieldProps {
  /** Omitted where the caller already renders one. */
  label?: string;
  value: AttachmentValue;
  onChange: (next: AttachmentValue) => void;
  /** Parameters declared on the notification — what the Dynamic dropdown offers. */
  parameterNames?: string[];
  accept?: string;
  /** Where a chosen file is sent. WhatsApp has its own endpoint, and the gateway
   *  resolves the fileId it returns — posting that file to the email attachment
   *  store would yield a reference WhatsApp cannot fetch. */
  upload?: (file: File) => Promise<UploadedAttachment>;
  /** Lets a channel warn about an upload that succeeded but is unusable — a
   *  WhatsApp file with no public URL, say. Returning null means all is well. */
  uploadWarning?: (uploaded: UploadedAttachment) => string | null;
  testId: string;
}

const FILE_ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,application/pdf,' +
  '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';

/**
 * One attachment, asked for the same way on every channel.
 *
 * Two modes, side by side:
 *
 *   Static   settled now — a file uploaded here, or a link to one
 *   Dynamic  a declared {parameter}, resolved when the notification fires
 *
 * The initial mode follows the stored value, since a parameter is unmistakable:
 * anything else is a file or a link, and both are static. Switching clears what
 * was held, because the value belongs to the mode that asked for it.
 */
export const AttachmentField = ({
  label, value, onChange, parameterNames = [], accept = FILE_ACCEPT,
  upload: uploadFile = uploadAttachment, uploadWarning, testId,
}: AttachmentFieldProps) => {
  const [mode, setMode] = useState<'static' | 'dynamic'>(
    value.kind === 'parameter' ? 'dynamic' : 'static',
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    setWarning(null);
    try {
      const done = await uploadFile(file);
      onChange({ kind: 'file', file: done });
      setWarning(uploadWarning?.(done) ?? null);
    } catch (e) {
      setError(extractErrorMessage(e, 'The file could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  };

  const chip = (() => {
    if (value.kind === 'parameter') {
      return { title: `{${value.name}}`, sub: 'supplied when the notification fires', detail: null };
    }
    if (value.kind === 'url') {
      return { title: value.url, sub: 'link', detail: null };
    }
    if (value.kind === 'file') {
      const f = value.file;
      return {
        title: f.filename,
        sub: [f.mimeType, f.sizeBytes ? `${Math.max(1, Math.round(f.sizeBytes / 1024))} KB` : null]
          .filter(Boolean).join(' · '),
        detail: f.fileId ? `fileId: ${f.fileId}` : null,
      };
    }
    return null;
  })();

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      ) : null}

      {/* Under the heading, because the choice comes first: it decides what the
          rest of the field even asks for. Radios rather than buttons since the
          two are exclusive states of one setting, not two actions. */}
      <div className="flex flex-wrap items-center gap-4">
        {([
          ['static', 'Static', 'a file settled now'],
          ['dynamic', 'Dynamic', 'a parameter resolved when it fires'],
        ] as const).map(([key, text, why]) => (
          <label key={key} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <RadioButton
              inputId={`${testId}.mode.${key}`}
              name={`${testId}.mode`}
              value={key}
              checked={mode === key}
              data-testid={`${testId}.mode.${key}`}
              onChange={() => {
                setMode(key);
                // The held value belongs to the mode being left, so it goes with
                // it. Keeping it would leave a parameter saved while the field
                // shows a file picker, and the author no way to see what is set.
                onChange({ kind: 'none' });
                setError(null);
                setWarning(null);
              }}
            />
            {text}
            <span className="text-xs text-gray-400">{why}</span>
          </label>
        ))}
      </div>

      {mode === 'static' ? (
        <>
          {/* A file to upload, or a link to one that already exists — both settle
              the attachment now, which is what makes them the static pair. The
              URL box is not decoration: every push `image` and WhatsApp media
              reference saved so far is a plain URL, and without it none of them
              could be read back or edited. Half each, since they answer one
              question; whichever is in use disables the other. */}
          <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <input
              type="file"
              disabled={uploading || value.kind === 'url'}
              accept={accept}
              className="w-full text-xs text-gray-600 sm:flex-1 sm:min-w-0 dark:text-gray-300"
              data-testid={`${testId}.upload`}
              onChange={async (e) => {
                const chosen = e.target.files?.[0];
                // Cleared first so the same file can be picked again after a failure.
                e.target.value = '';
                if (chosen) await upload(chosen);
              }}
            />
            <InputText
              value={value.kind === 'url' ? value.url : ''}
              className="w-full font-mono text-xs sm:flex-1 sm:min-w-0"
              placeholder="…or paste a URL: https://…"
              disabled={value.kind === 'file'}
              data-testid={`${testId}.url`}
              onChange={(e) => {
                const next = e.target.value;
                onChange(next.trim() ? { kind: 'url', url: next } : { kind: 'none' });
              }}
            />
          </div>
          {uploading && <small className="text-xs text-gray-500">Uploading…</small>}
          <small className="text-xs text-gray-400">
            Sent with every notification of this kind. Clear one to use the other.
          </small>
        </>
      ) : (
        <>
          <Dropdown
            value={value.kind === 'parameter' ? value.name : null}
            options={parameterNames.map((n) => ({ label: `{${n}}`, value: n }))}
            optionLabel="label" optionValue="value"
            className="w-full" showClear
            placeholder={parameterNames.length
              ? 'Use a declared parameter…'
              : 'No parameters declared above yet'}
            disabled={!parameterNames.length}
            data-testid={`${testId}.parameter`}
            onChange={(e) => onChange(e.value ? { kind: 'parameter', name: e.value } : { kind: 'none' })}
          />
          <small className="text-xs text-gray-400">
            The fire supplies this one. Declare the parameter above, and send a URL
            or a file reference in it — nothing is attached when the fire omits it.
          </small>
        </>
      )}

      {chip && (
        <div className="flex items-start justify-between gap-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs dark:border-gray-700 dark:bg-gray-800">
          <div className="min-w-0">
            <p className="truncate font-medium">{chip.title}</p>
            <p className="text-gray-500">{chip.sub}</p>
            {chip.detail && <p className="break-all text-gray-400">{chip.detail}</p>}
          </div>
          <button aria-label="Remove" type="button" title="Remove" className="shrink-0 text-gray-400 hover:text-red-500"
                  data-testid={`${testId}.remove`}
                  onClick={() => { onChange({ kind: 'none' }); setError(null); setWarning(null); }}>
            <i className="pi pi-times" />
          </button>
        </div>
      )}

      {warning && <Message severity="warn" className="w-full" text={warning} />}
      {error && <Message severity="error" className="w-full" text={error} />}
    </div>
  );
};

export default AttachmentField;
